import type { ApplicationSubmissionDocument, ProductionDocument } from '@/types'
import { INVITATION_LABEL, invitationState, selectionWorkflow } from '@/types/selectionWorkflow'

export function InvitationStatus({
  submission,
  showResponse = true,
}: {
  submission: ApplicationSubmissionDocument
  showResponse?: boolean
}) {
  const state = invitationState(submission)
  if (submission.status !== 'accepted' && state === 'not_sent') return null
  const sentAt =
    state === 'sent' && !submission.participantInvitedAt
      ? submission.confirmedAt?.toDate?.()
      : undefined
  return (
    <div className="space-y-1 text-sm">
      <span
        className={`block font-medium ${state === 'sent' ? 'text-emerald-700 dark:text-emerald-400' : state === 'uncertain' || state === 'cancelled' ? 'text-destructive' : 'text-muted-foreground'}`}
      >
        {INVITATION_LABEL[state]}
      </span>
      {sentAt && (
        <time className="block text-muted-foreground" dateTime={sentAt.toISOString()}>
          {sentAt.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short',
          })}
        </time>
      )}
      {showResponse && state === 'sent' && (
        <span className="block text-muted-foreground">
          {submission.participantWithdrawnAt
            ? 'Artist withdrew'
            : submission.participantDeclinedAt
              ? 'Artist declined'
              : submission.participantAcceptedAt
                ? 'Artist accepted'
                : 'Awaiting artist response'}
        </span>
      )}
    </div>
  )
}

export function InvitationProgress({
  production,
  submissions,
  loading,
  current,
  error,
}: {
  production: ProductionDocument
  submissions: ApplicationSubmissionDocument[]
  loading: boolean
  current: boolean
  error?: string | null
}) {
  const workflow = selectionWorkflow(production, submissions)
  const title = loading
    ? 'Checking invitation status…'
    : error
      ? 'Invitation status unavailable'
      : workflow.recipientCount
        ? `${workflow.counts.sent} of ${workflow.recipientCount} acceptance emails sent`
        : 'No applicants selected for invitation'
  return (
    <section
      aria-label="Acceptance email status"
      aria-live="polite"
      className="space-y-2 rounded-md border bg-muted/30 p-4"
    >
      <h2 className="font-semibold">{title}</h2>
      {!loading && !error && (
        <>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
            <span>{workflow.newInvitationCount} not sent</span>
            <span>{workflow.processing} queued or retrying</span>
            <span>{workflow.attention} need review</span>
          </div>
          <p className="text-sm text-muted-foreground">
            {workflow.state === 'closed'
              ? 'This production is closed. Sending is disabled.'
              : workflow.state === 'sent'
                ? 'All acceptance emails have been sent. No acceptance emails remain to send.'
                : workflow.processing
                  ? 'Queued invitations continue automatically. Progress updates here as they are processed.'
                  : 'Selecting an applicant prepares their invitation. Review and send new acceptances when the slate is ready.'}
          </p>
          {workflow.attention > 0 && (
            <p className="text-sm text-destructive">
              Review cancelled or uncertain invitations in Participants. These cannot be resent from
              curation.
            </p>
          )}
          {workflow.counts.sent > 0 && (
            <p className="text-sm text-muted-foreground">
              Sent means the email provider accepted the message. Artist responses and required
              materials are tracked separately in Participants.
            </p>
          )}
        </>
      )}
      {!loading && (!current || error) && (
        <p role="alert" className="text-sm text-destructive">
          Live status is unavailable. Sending is disabled until the connection is restored.
        </p>
      )}
    </section>
  )
}
